import { NxMultiStepperComponent } from "@allianz/ng-aquila/progress-stepper";
import {
  computed,
  DestroyRef,
  effect,
  inject,
  Injectable,
  signal,
} from "@angular/core";
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from "@angular/core/rxjs-interop";
import {
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { NavigationEnd, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { connect } from "ngxtension/connect";
import { createNotifier } from "ngxtension/create-notifier";
import { ifValidator } from "ngxtension/if-validator";
import {
  debounceTime,
  filter,
  map,
  merge,
  of,
  Subject,
  switchMap,
  tap,
} from "rxjs";
import {
  ProgressStep,
  progressStepRoutingPathMap,
} from "../../components/process/ui/progress-bar/progress-bar-stepes.enum";
import { environment } from "../../environments/environment";
import { AppType } from "../enums/app-type.enum";
import { PolicyType } from "../enums/policy-type.enum";
import { RoutingPaths } from "../enums/routing-paths.enum";
import { Agreement } from "../interfaces/agreements.interface";
import { LogRequest } from "../interfaces/analytics.interface";
import { ApkQuestion } from "../interfaces/apk-questions.interface";
import { InsuranceDataForm } from "../interfaces/insurance-data.interface";
import {
  InsuranceVariant,
  InsuranceVariantType,
} from "../interfaces/insurance-variant";
import { InsuranceVariantForm } from "../interfaces/insurance-variant-form.interface";
import { NeedsAnalysisForm } from "../interfaces/needs-analysis-form.interface";
import { PostPurchaseBody } from "../interfaces/purchase.interface";
import { PostQuotationResponse } from "../interfaces/quotation.interface";
import {
  SetStepValidity,
  StepsValidity,
} from "../interfaces/step-validity.interface";
import { ApiService } from "../services/api.service";
import { LoaderService } from "../services/loader.service";
import { MessageService } from "../services/message.service";
import { PaymentService } from "../services/payment.service";
import { insuranceDurationValidator } from "../validators/insurance-duration.validator";
import {
  PeselOver80Validator,
  PeselValidator,
} from "../validators/pesel.validator";
import PhoneNumberValidator from "../validators/phone-number.validator";
import PostCodeValidator from "../validators/post-code.validator";
import { emailSameValidator } from "../validators/same.validator";
import { PolicyStore } from "./policy.store";

export interface ProcessState {
  stepper: NxMultiStepperComponent | null;
  stepValidity: StepsValidity;
  insuranceVariants: InsuranceVariant[];
  quotationResponse: PostQuotationResponse | null;
  selectedVariant: InsuranceVariant | null;
  apkQuestions: ApkQuestion[] | null;
  agreements: Agreement[] | null;
}

@Injectable({
  providedIn: "root",
})
export class ProcessStore {
  // const
  private readonly initialValues: ProcessState = {
    stepper: null,
    stepValidity: {
      [ProgressStep.NEEDS_ANALYSIS]: {
        valid: false,
        message: "needs-analysis.wrong-answers-validation",
      },
      [ProgressStep.INSURANCE_VARIANT]: {
        valid: false,
        message: "insurance-variant.no-variant-selected",
        nextButtonTitle: "navigation-button.chose-variant",
      },
      [ProgressStep.INSURANCE_DATA]: {
        valid: false,
        message: "insurance-data.correct-form",
        nextButtonTitle:
          environment.appType === AppType.Direct
            ? "navigation-button.go-to-payment"
            : "navigation-button.create-policy",
      },
      [ProgressStep.PAYMENT]: { valid: false },
      [ProgressStep.CREATE_POLICY]: { valid: false, hideButtons: true },
    },
    insuranceVariants: [
      {
        name: InsuranceVariantType.Me,
        amount: null,
        isHightlighted: false,
      },
      {
        name: InsuranceVariantType.Family,
        amount: null,
        isHightlighted: false,
      },
      {
        name: InsuranceVariantType.Standard,
        amount: null,
        isHightlighted: false,
      },
    ],
    quotationResponse: null,
    selectedVariant: null,
    apkQuestions: null,
    agreements: null,
  };
  readonly processSteps =
    environment.appType === AppType.Direct
      ? [
          { index: 0, value: ProgressStep.NEEDS_ANALYSIS },
          { index: 1, value: ProgressStep.INSURANCE_VARIANT },
          { index: 2, value: ProgressStep.INSURANCE_DATA },
          { index: 3, value: ProgressStep.PAYMENT },
        ]
      : [
          { index: 0, value: ProgressStep.NEEDS_ANALYSIS },
          { index: 1, value: ProgressStep.INSURANCE_VARIANT },
          { index: 2, value: ProgressStep.INSURANCE_DATA },
          { index: 3, value: ProgressStep.CREATE_POLICY },
        ];
  private readonly stateChangeNotifier = createNotifier();

  // forms
  needsAnalysisForm!: FormGroup<NeedsAnalysisForm>;
  insuranceVariantForm!: FormGroup<InsuranceVariantForm>;
  insuranceDataForm!: FormGroup<InsuranceDataForm>;

  // services
  private readonly fb = inject(FormBuilder);
  private readonly apiService = inject(ApiService);
  private readonly paymentService = inject(PaymentService);
  private readonly loaderService = inject(LoaderService);
  private readonly router = inject(Router);
  private readonly policyStore = inject(PolicyStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);

  // state
  private state = signal<ProcessState>({ ...this.initialValues });

  // selectors
  private readonly stepper = computed(() => this.state().stepper);
  readonly apkQuestions = computed(() => this.state().apkQuestions);
  readonly agreements = computed(() => this.state().agreements);
  readonly insuranceDateFrom = computed(
    () => this.state().quotationResponse?.dateFrom,
  );
  readonly insuranceDateTo = computed(
    () => this.state().quotationResponse?.dateTo,
  );
  readonly currentStep = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects.split("/").pop()),
      map((lastSegment) => {
        return progressStepRoutingPathMap.getKey(
          lastSegment as RoutingPaths,
        ) as ProgressStep;
      }),
    ),
  );
  readonly hasNextStep = computed(() => {
    this.stateChangeNotifier.listen();
    return this.stepper()?.hasNext;
  });
  readonly hasPreviousStep = computed(() => {
    this.stateChangeNotifier.listen();
    return this.stepper()?.hasPrevious;
  });

  readonly stepValidity = computed(() => this.state().stepValidity);
  readonly currentStepValidity = computed(
    () => this.stepValidity()[this.currentStep()!],
  );
  readonly insuranceVariants = computed(() => this.state().insuranceVariants);
  readonly selectedVariant = computed(() => this.state().selectedVariant);
  readonly currentPolicyType = computed(() =>
    this.policyStore.currentPolicyType$(),
  );

  readonly termsAndConditionsDocumentUrl = computed(() => {
    const baseURL = location.href.replace(this.router.url, "");
    return `${baseURL}/termsAndConditions${this.currentPolicyType()}.pdf`;
  });
  readonly authorizationDocumentUrl = computed(() => {
    const baseURL = location.href.replace(this.router.url, "");
    return `${baseURL}/authorizationDocument.pdf`;
  });

  // events
  setStepper$ = new Subject<NxMultiStepperComponent | null>();
  setStepValidity$ = new Subject<SetStepValidity>();
  setApkQuestions$ = new Subject<ApkQuestion[]>();
  setAgreements$ = new Subject<Agreement[]>();
  setQuotationResponse$ = new Subject<PostQuotationResponse | null>();
  nextStep$ = new Subject<void>();
  previousStep$ = new Subject<void>();
  fetchApkQuestions$ = new Subject<void>();
  fetchVariantsPrices$ = new Subject<void>();
  fetchAgreements$ = new Subject<void>();
  purchase$ = new Subject<void>();
  purchaseAgent$ = new Subject<void>();
  setInsuranceVariants$ = new Subject<InsuranceVariant[]>();
  setSelectedVariant$ = new Subject<InsuranceVariant | null>();
  downloadOwuDocument$ = new Subject<void>();

  //observable
  readonly fetchVariantsPricesObservable$ = of(true).pipe(
    takeUntilDestroyed(),
    filter(
      () => this.needsAnalysisForm.valid && this.insuranceVariantForm.valid,
    ),
    switchMap(() =>
      this.apiService.postQuotation({
        dateFrom: moment().add(1, "day").toDate(),
        duration: this.insuranceVariantForm.getRawValue().duration!,
        apkAnswers: this.needsAnalysisForm
          .getRawValue()
          .questions.map((value, index) =>
            value ? this.apkQuestions()![index].agreementId : null,
          )
          .filter((value) => value !== null),
      }),
    ),
    tap((quotationResp) => {
      this.setQuotationResponse$.next(quotationResp);
      this.setStepValidity$.next({
        step: ProgressStep.NEEDS_ANALYSIS,
        valid: !quotationResp.isOfferPresentationBlocked,
        message: quotationResp.isOfferPresentationBlocked
          ? "needs-analysis.wrong-answers-validation"
          : undefined,
      });
      this.setInsuranceVariants$.next(quotationResp.variants);
      if (this.selectedVariant()) {
        const variant = quotationResp.variants.find(
          (variant) => variant.name === this.selectedVariant()!.name,
        );
        this.setSelectedVariant$.next(variant!);
      }
    }),
  );

  constructor() {
    this.router.events
      .pipe(
        takeUntilDestroyed(),
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          const logRequest: LogRequest = {
            message: "[routeOpen] " + document.URL,
            webAppName: "SCB",
            logLevel: "Information",
            overridedReferer: document.URL,
            eventDetails: {
              url: document.URL,
              policy: this.policyStore.currentPolicyType,
              appType: environment.appType === AppType.Direct ? "Direct" : "Agent",
            },
          };

          return logRequest;
        }),
        switchMap((logRequest) =>
          this.apiService.postAnalyticsData(logRequest),
        ),
      )
      .subscribe();

    this.initForms();

    // reducers
    connect(this.state)
      .with(this.setStepper$, (prev, stepper) => ({
        ...prev,
        stepper,
      }))
      .with(this.setStepValidity$, (prev, stepValidity) => ({
        ...prev,
        stepValidity: {
          ...prev.stepValidity,
          [stepValidity.step]: stepValidity,
        },
      }))
      .with(this.setSelectedVariant$, (prev, selectedVariant) => ({
        ...prev,
        selectedVariant,
      }))
      .with(this.setQuotationResponse$, (prev, quotationResponse) => ({
        ...prev,
        quotationResponse,
      }))
      .with(this.setAgreements$, (prev, agreements) => ({
        ...prev,
        agreements,
      }))
      .with(this.setApkQuestions$, (prev, apkQuestions) => ({
        ...prev,
        apkQuestions,
      }))
      .with(this.setInsuranceVariants$, (prev, insuranceVariants) => ({
        ...prev,
        insuranceVariants,
      }));

    effect(() => {
      switch (this.currentStep()) {
        case ProgressStep.NEEDS_ANALYSIS:
          if (!this.apkQuestions()) {
            this.fetchApkQuestions$.next();
          }
          break;
        case ProgressStep.INSURANCE_VARIANT:
          this.fetchVariantsPrices$.next();
          break;
        case ProgressStep.INSURANCE_DATA:
          if (!this.agreements()) {
            this.fetchAgreements$.next();
          }
          break;
        case ProgressStep.PAYMENT:
          this.purchase$.next();
          break;
        case ProgressStep.CREATE_POLICY:
          this.purchaseAgent$.next();
          break;

        default:
          break;
      }
    });

    this.nextStep$
      .pipe(
        takeUntilDestroyed(),
        switchMap(() => {
          if (this.currentStep() === ProgressStep.NEEDS_ANALYSIS) {
            if (!this.needsAnalysisForm.valid) {
              this.setStepValidity$.next({
                step: ProgressStep.NEEDS_ANALYSIS,
                valid: false,
                message: "needs-analysis.answer-all-questions",
              });

              return of(true);
            }
            return this.fetchVariantsPricesObservable$;
          }

          return of(true);
        }),
        tap(() => {
          if (!this.currentStepValidity().valid) {
            this.messageService.show(
              "error",
              this.translateService.instant(
                this.currentStepValidity().message!,
              ),
            );
          } else {
            setTimeout(() => {
              this.stepper()?.next();
            });
          }
        }),
      )
      .subscribe();

    this.previousStep$
      .pipe(
        takeUntilDestroyed(),
        tap(() => {
          this.stepper()?.previous();
        }),
      )
      .subscribe();

    this.setSelectedVariant$
      .pipe(
        takeUntilDestroyed(),
        tap(() => {
          this.checkInsuranceVariantsStepValidity();
        }),
      )
      .subscribe();

    this.fetchApkQuestions$
      .pipe(
        takeUntilDestroyed(),
        tap(() => {
          this.loaderService.show();
        }),
        switchMap(() => this.apiService.getApkQuestions()),
        tap((apkQuestions) => {
          this.loaderService.hide();
          this.setApkQuestions$.next(apkQuestions.apkQuestions);
          this.initNeedsAnalysisForm();
        }),
      )
      .subscribe();

    this.fetchAgreements$
      .pipe(
        takeUntilDestroyed(),
        tap(() => {
          this.loaderService.show();
        }),
        switchMap(() => this.apiService.getAgreements()),
        tap((agreementsResp) => {
          this.setAgreements$.next(agreementsResp.agreements);
          this.loaderService.hide();
          this.initInsuranceDataForm();
        }),
      )
      .subscribe();

    this.purchase$
      .pipe(
        takeUntilDestroyed(),
        switchMap(() =>
          this.apiService.postPurchase(this.preparePurchaseBody()),
        ),
        switchMap((purchaseResponse) =>
          this.paymentService.openTransaction(
            purchaseResponse.hashCode,
            purchaseResponse.clientEmail,
          ),
        ),
      )
      .subscribe();

    this.purchaseAgent$
      .pipe(
        takeUntilDestroyed(),
        tap(() => this.loaderService.show()),
        switchMap(() =>
          this.apiService.postPurchase(this.preparePurchaseBody()),
        ),
        tap(() => this.loaderService.hide()),
      )
      .subscribe();

    this.downloadOwuDocument$
      .pipe(
        takeUntilDestroyed(),
        tap(() => {
          window.open(this.apiService.owuDocumentUrl());
        }),
      )
      .subscribe();

    toObservable(this.stepper)
      .pipe(
        filter((stepper) => !!stepper),
        switchMap((stepper) => stepper.selectionChange.asObservable()),
        tap((event) => {
          const policy =
            this.policyStore.currentPolicyType?.toLocaleLowerCase();
          const newProgressStep = this.processSteps.find(
            (step) => step.index === event.selectedIndex,
          )?.value;
          if (newProgressStep) {
            const routToStep = progressStepRoutingPathMap.get(
              newProgressStep as ProgressStep,
            )!;
            this.router.navigate([RoutingPaths.scb, policy, routToStep]);
          }

          this.stateChangeNotifier.notify();
        }),
      )
      .subscribe();

    this.fetchVariantsPrices$
      .pipe(switchMap(() => this.fetchVariantsPricesObservable$))
      .subscribe();
  }

  initForms() {
    this.initInsuranceVariantForm();
  }

  private initNeedsAnalysisForm() {
    this.needsAnalysisForm = this.fb.group<NeedsAnalysisForm>({
      questions: this.fb.array(
        this.apkQuestions()?.map((question) => {
          return this.fb.control<boolean | null>(null, Validators.required);
        })!,
      ),
    });
  }

  private initInsuranceVariantForm() {
    this.insuranceVariantForm = this.fb.group<InsuranceVariantForm>({
      duration: this.fb.control(1, {
        validators: [insuranceDurationValidator()],
      }),
    });

    this.insuranceVariantForm.statusChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(100),
        tap(() => {
          this.checkInsuranceVariantsStepValidity();
          this.fetchVariantsPrices$.next();
        }),
      )
      .subscribe();
  }

  private initInsuranceDataForm() {
    const emailControl = this.fb.control(null, [
      Validators.required,
      Validators.email,
    ]);

    this.insuranceDataForm = this.fb.group<InsuranceDataForm>({
      firstName: this.fb.control(null, [Validators.required]),
      secondName: this.fb.control(null, [Validators.required]),
      pesel: this.fb.control(null, [
        Validators.required,
        PeselValidator,
        ifValidator(
          () => this.currentPolicyType() === PolicyType.MyHealth,
          [PeselOver80Validator],
        ),
      ]),
      phone: this.fb.control(null, [Validators.required, PhoneNumberValidator]),
      email: emailControl,
      repeatEmail: this.fb.control(null, [
        Validators.required,
        emailSameValidator(emailControl),
      ]),
      postalCode: this.fb.control(null, [
        Validators.required,
        PostCodeValidator,
      ]),
      city: this.fb.control(null, [Validators.required]),
      street: this.fb.control(null),
      building: this.fb.control(null, [Validators.required]),
      apartment: this.fb.control(null),

      sameInsuredData: this.fb.control(null, [
        this.policyStore.currentPolicyType === PolicyType.MyHome
          ? Validators.required
          : Validators.nullValidator,
      ]),
      insuredFirstName: this.fb.control(null, [
        this.policyStore.currentPolicyType === PolicyType.MyHome
          ? Validators.required
          : Validators.nullValidator,
      ]),
      insuredSecondName: this.fb.control(null, [
        this.policyStore.currentPolicyType === PolicyType.MyHome
          ? Validators.required
          : Validators.nullValidator,
      ]),
      agreements: this.fb.array(
        this.agreements()!.map((agreement) => {
          const validators: ValidatorFn[] = [Validators.required];
          if (agreement.isRequired) {
            validators.push(Validators.requiredTrue);
          }

          return this.fb.control<boolean | null>(false, {
            validators,
          });
        }),
      ),
    });

    this.insuranceDataForm.controls.email.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.insuranceDataForm.controls.repeatEmail.updateValueAndValidity();
        }),
      )
      .subscribe();

    merge(
      this.insuranceDataForm.controls.sameInsuredData.valueChanges,
      this.insuranceDataForm.controls.firstName.valueChanges,
      this.insuranceDataForm.controls.secondName.valueChanges,
      this.insuranceDataForm.controls.pesel.valueChanges,
    )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          if (this.insuranceDataForm.controls.sameInsuredData.value) {
            this.insuranceDataForm.controls.insuredFirstName.setValue(
              this.insuranceDataForm.controls.firstName.value,
            );
            this.insuranceDataForm.controls.insuredSecondName.setValue(
              this.insuranceDataForm.controls.secondName.value,
            );
          } else {
            this.insuranceDataForm.controls.insuredFirstName.reset();
            this.insuranceDataForm.controls.insuredSecondName.reset();
          }
        }),
      )
      .subscribe();

    this.insuranceDataForm.statusChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((status) => {
          let message = undefined;
          if (status === "INVALID") {
            const formFieldNames = Object.entries(
              this.insuranceDataForm.controls,
            );

            const isAgreementsInvalid =
              this.insuranceDataForm.controls.agreements.invalid;
            const isFormInvalid = formFieldNames
              .filter(([key, control]) => key !== "agreements")
              .map(([key, control]) => control.invalid)
              .some((isInvalid) => !!isInvalid);

            if (isFormInvalid) {
              message = "insurance-data.correct-form";
            } else if (isAgreementsInvalid) {
              message = "insurance-data.accept-agreements";
            }
          }

          this.setStepValidity$.next({
            step: ProgressStep.INSURANCE_DATA,
            valid: status === "VALID",
            nextButtonTitle:
              environment.appType === AppType.Direct
                ? "navigation-button.go-to-payment"
                : "navigation-button.create-policy",
            message: message,
          });
        }),
      )
      .subscribe();
  }

  private checkInsuranceVariantsStepValidity() {
    const isFormValid = this.insuranceVariantForm.valid;
    const isVariantChecked = !!this.selectedVariant();

    if (!isFormValid) {
      this.setStepValidity$.next({
        step: ProgressStep.INSURANCE_VARIANT,
        valid: false,
        message: "insurance-variant.wrong-insurance-duration",
        nextButtonTitle: "navigation-button.chose-variant",
      });
      return;
    } else if (!isVariantChecked) {
      this.setStepValidity$.next({
        step: ProgressStep.INSURANCE_VARIANT,
        valid: false,
        message: "insurance-variant.no-variant-selected",
        nextButtonTitle: "navigation-button.chose-variant",
      });
      return;
    }

    this.setStepValidity$.next({
      step: ProgressStep.INSURANCE_VARIANT,
      valid: true,
      message: undefined,
      nextButtonTitle: "navigation-button.chose-variant",
    });
  }

  private preparePurchaseBody(): PostPurchaseBody {
    const insuranceData = this.insuranceDataForm.getRawValue();
    const insuranceVariant = this.insuranceVariantForm.getRawValue();
    const needsAnalysis = this.needsAnalysisForm.getRawValue();

    const purchaseBody: PostPurchaseBody = {
      policyholder: {
        firstName: insuranceData.firstName!,
        lastName: insuranceData.secondName!,
        pesel: insuranceData.pesel!,
        phone: insuranceData.phone!,
        email: insuranceData.email!,
        street: insuranceData.street!,
        houseNumber: insuranceData.building!,
        apartmentNumber: insuranceData.apartment!,
        city: insuranceData.city!,
        postalCode: insuranceData.postalCode!,
      },
      insured:
        this.policyStore.currentPolicyType === PolicyType.MyHome
          ? {
              thirdPartyInsured: !insuranceData.sameInsuredData,
              firstName: insuranceData.insuredFirstName!,
              lastName: insuranceData.insuredSecondName!,
            }
          : undefined,
      coverage: {
        dateFrom: moment().add(1, "day").toDate(),
        duration: insuranceVariant.duration!,
        variant: this.selectedVariant()?.name!,
      },
      apkAnswers: needsAnalysis.questions
        .map((value, index) =>
          value ? this.apkQuestions()![index].agreementId : null,
        )
        .filter((value) => value !== null),
      agreements: insuranceData.agreements
        .map((value, index) =>
          value ? this.agreements()![index].agreementId : null,
        )
        .filter((value) => value !== null),
    };

    return purchaseBody;
  }
}
